import * as React from "react"
import { jsonFetch } from "../bookingApi"
import { clearCheckoutSessionStorage } from "../bookingSession"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "setIsMobile" | "abortController" | "addressLookupController" | "calendarController" | "dateNoticeTimer" | "draftSaveTimer" | "draftExpiryTimer" | "billingElementRef" | "expressElementRef" | "paymentElementRef" | "checkoutGeneration" | "checkoutRef" | "actionsRef" | "setClientSecret" | "setConfirmationToken" | "setStripeReady" | "setStripeCanConfirm" | "setSavedDetailsFingerprint" | "confirmationToken" | "initialDraftRef" | "setQuote" | "availabilityController" | "setPhase">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "baseUrl">

export function useCheckoutLifecycle(inputs: Inputs) {
    const {
        setIsMobile,
        abortController,
        addressLookupController,
        calendarController,
        dateNoticeTimer,
        draftSaveTimer,
        draftExpiryTimer,
        billingElementRef,
        expressElementRef,
        paymentElementRef,
        checkoutGeneration,
        checkoutRef,
        actionsRef,
        setClientSecret,
        setConfirmationToken,
        setStripeReady,
        setStripeCanConfirm,
        setSavedDetailsFingerprint,
        confirmationToken,
        initialDraftRef,
        setQuote,
        baseUrl,
        availabilityController,
        setPhase,
    } = inputs
    React.useEffect(() => {
        if (typeof window === "undefined") return
        const media = window.matchMedia("(max-width: 699px)")
        const update = () => setIsMobile(media.matches)
        update()
        media.addEventListener("change", update)
        return () => media.removeEventListener("change", update)
    }, [])

    React.useEffect(() => () => {
        abortController.current?.abort()
        addressLookupController.current?.abort()
        calendarController.current?.abort()
        if (dateNoticeTimer.current !== null) window.clearTimeout(dateNoticeTimer.current)
        if (draftSaveTimer.current !== null) window.clearTimeout(draftSaveTimer.current)
        if (draftExpiryTimer.current !== null) window.clearTimeout(draftExpiryTimer.current)
        // Each Stripe Element hook owns its unmount/destroy cleanup. Destroying
        // them here too makes subsequent hook cleanup call a destroyed Element.
    }, [])

    const resetCheckout = React.useCallback(() => {
        checkoutGeneration.current += 1
            ;[billingElementRef, expressElementRef, paymentElementRef].forEach((ref) => { ref.current?.unmount(); ref.current = null })
        checkoutRef.current = null
        actionsRef.current = null
        setClientSecret("")
        setConfirmationToken("")
        setStripeReady(false)
        setStripeCanConfirm(false)
        setSavedDetailsFingerprint("")
    }, [])

    const invalidateCheckout = React.useCallback(() => {
        const staleToken = confirmationToken
        initialDraftRef.current = null
        resetCheckout()
        clearCheckoutSessionStorage()
        setQuote(null)
        if (staleToken) {
            void jsonFetch(`${baseUrl}/v1/cancel-checkout`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ confirmationToken: staleToken }),
            }).catch(() => { /* Server-side expiry releases an unreachable session. */ })
        }
    }, [baseUrl, confirmationToken, resetCheckout])

    const cancelAvailabilityCheck = React.useCallback(() => {
        availabilityController.current?.abort()
        availabilityController.current = null
        setPhase((current) => current === "checking" ? "ready" : current)
    }, [])

    return {
        resetCheckout,
        invalidateCheckout,
        cancelAvailabilityCheck,
    }
}
